'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { Star } from 'phosphor-icons';
import { useCallback, useMemo } from 'react';
import { CheckboxWithLabel, c } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { RATING_RANGES, getRatingController } from '../facetControllers';
import { useOptimisticFacetSelection } from '../hooks/useOptimisticFacetSelection';
import { useStableFacetValues } from '../hooks/useStableFacetValues';
import { RatingFacetProps } from './RatingFacet.types';

export const RatingFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: RatingFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const ratingLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.rating.title')
      ?.value ?? 'Rating';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.rating.clear')
      ?.value ?? 'Clear';

  const eng = useEngine();
  const controller = useMemo(() => getRatingController(eng), [eng]);
  const state = useController(controller);
  const stableValues = useStableFacetValues(state.values, 'ec_rating', eng);
  const { optimisticValues, selectedCount, findStableValue, applyOptimistic } =
    useOptimisticFacetSelection(stableValues);

  const getLabel = (start: number, end: number) => {
    const range = RATING_RANGES.find((r) => r.start === start && r.end === end);
    if (!range) return `${start}-${end}`;
    const entry = searchLabelsMicrocopy?.find((m) => m.key === range.key);
    return entry?.value ?? range.label;
  };

  const handleClear = useCallback(() => {
    controller.deselectAll();
  }, [controller]);

  const isHorizontal = variant === 'horizontal';
  const isDark = variant === 'mobile';

  const content = (
    <div className="flex flex-col">
      {[...optimisticValues]
        .sort((a, b) => b.start - a.start)
        .map((facetValue) => (
          <CheckboxWithLabel
            key={`rating-${facetValue.start}-${facetValue.end}`}
            id={`rating-${facetValue.start}-${facetValue.end}`}
            checked={facetValue.state === 'selected'}
            disabled={
              facetValue.numberOfResults === 0 &&
              facetValue.state !== 'selected'
            }
            onCheckedChange={() => {
              const stableValue = findStableValue(facetValue);
              if (!stableValue) return;
              applyOptimistic([stableValue], () =>
                controller.toggleSelect(stableValue)
              );
              controller.toggleSelect(stableValue);
            }}
            size={isHorizontal ? undefined : 'sm'}
            isDarkTheme={isDark}
          >
            <Star
              size={16}
              weight="fill"
              className={c(
                'shrink-0',
                isDark ? 'text-grey-400' : 'text-grey-700'
              )}
            />
            <span className="flex-1">
              {getLabel(facetValue.start, facetValue.end)}
            </span>
            <span
              className={c(
                'rounded px-1 text-xs leading-5 tracking-tight',
                isDark
                  ? 'bg-grey-800 text-grey-400'
                  : 'bg-grey-100 text-grey-700'
              )}
            >
              {facetValue.numberOfResults.toLocaleString()}
            </span>
          </CheckboxWithLabel>
        ))}
    </div>
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={ratingLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={ratingLabel}
      variant={variant}
      className={className}
      onClear={handleClear}
      clearLabel={clearLabel}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
